import React, { useState, useEffect } from 'react';
import { Calendar, X, Image as ImageIcon, Info, CheckCircle, Clock, Plus, Trash2, Edit, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { formatImageUrl } from '../utils/urlHelper';

const GanttDashboard = () => {
    const [photos, setPhotos] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskPhotos, setTaskPhotos] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [loadingPhotos, setLoadingPhotos] = useState(false);

    const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';
    const activeProjectId = localStorage.getItem('activeProjectId');

    const [tasks, setTasks] = useState([]);

    // Roles support
    const userRole = localStorage.getItem('userRole') || 'VIEWER';
    const isDemo = userRole === 'DEMO_USER';
    const canWrite = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'PROJECT_MANAGER';

    // CRUD/Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [taskName, setTaskName] = useState('');
    const [startDate, setStartDate] = useState('2026-06-01');
    const [endDate, setEndDate] = useState('2026-06-05');
    const [taskStatus, setTaskStatus] = useState('planned');
    const [taskProgress, setTaskProgress] = useState(0);
    const [formLoading, setFormLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchTasks = async () => {
        if (!activeProjectId) return;
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': activeProjectId
            };
            const tasksRes = await axios.get('http://localhost:4000/api/gantt', { headers });
            if (tasksRes.data && Array.isArray(tasksRes.data)) {
                setTasks(tasksRes.data);
            }
        } catch (err) {
            console.error("Error al cargar tareas:", err);
        }
    };

    useEffect(() => {
        const fetchGanttData = async () => {
            if (!activeProjectId) return;
            try {
                const headers = {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'x-project-id': activeProjectId
                };
                const [photosRes, tasksRes] = await Promise.all([
                    axios.get('http://localhost:4000/api/photos', { headers }),
                    axios.get('http://localhost:4000/api/gantt', { headers })
                ]);
                if (photosRes.data && Array.isArray(photosRes.data)) {
                    setPhotos(photosRes.data);
                }
                if (tasksRes.data && Array.isArray(tasksRes.data)) {
                    setTasks(tasksRes.data);
                }
            } catch (err) {
                console.error("Error al cargar datos para Gantt:", err);
            }
        };
        fetchGanttData();
    }, [activeProjectId]);

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setLoadingPhotos(true);
        setDrawerOpen(true);

        const start = new Date(task.start + 'T00:00:00');
        const end = new Date(task.end + 'T23:59:59');

        // Filter photos falling into this task's date range
        const filtered = photos.filter(photo => {
            const captured = new Date(photo.captured_at);
            return captured >= start && captured <= end;
        });

        setTaskPhotos(filtered);
        setLoadingPhotos(false);
    };

    const openAddModal = () => {
        if (!canWrite) {
            alert("Modo Demo Activo: Las modificaciones y escrituras están deshabilitadas de forma segura.");
            return;
        }
        setEditingTask(null);
        setTaskName('');
        setStartDate('2026-06-01');
        setEndDate('2026-06-05');
        setTaskStatus('planned');
        setTaskProgress(0);
        setErrorMsg('');
        setIsModalOpen(true);
    };

    const openEditModal = (task, e) => {
        e.stopPropagation(); // Avoid triggering drawer open
        if (!canWrite) {
            alert("Modo Demo Activo: Las modificaciones y escrituras están deshabilitadas de forma segura.");
            return;
        }
        setEditingTask(task);
        setTaskName(task.name);
        setStartDate(task.start);
        setEndDate(task.end);
        setTaskStatus(task.status);
        setTaskProgress(task.progress || 0);
        setErrorMsg('');
        setIsModalOpen(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!canWrite) return;
        if (!taskName.trim() || !startDate || !endDate) {
            setErrorMsg("Todos los campos obligatorios deben completarse.");
            return;
        }

        setFormLoading(true);
        setErrorMsg('');

        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': activeProjectId
            };
            const payload = {
                name: taskName,
                start_date: startDate,
                end_date: endDate,
                status: taskStatus,
                progress: parseInt(taskProgress, 10) || 0
            };

            if (editingTask) {
                await axios.put(`http://localhost:4000/api/gantt/${editingTask.id}`, payload, { headers });
            } else {
                await axios.post('http://localhost:4000/api/gantt', payload, { headers });
            }

            setIsModalOpen(false);
            await fetchTasks();
        } catch (err) {
            console.error("Error al guardar la tarea:", err);
            setErrorMsg(err.response?.data?.error || "Error al guardar la tarea");
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteTask = async (task, e) => {
        e.stopPropagation(); // Avoid triggering drawer open
        if (!canWrite) {
            alert("Modo Demo Activo: Las modificaciones y escrituras están deshabilitadas de forma segura.");
            return;
        }
        if (!window.confirm(`¿Estás seguro de que deseas eliminar la tarea "${task.name}"?`)) {
            return;
        }

        setActionLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': activeProjectId
            };
            await axios.delete(`http://localhost:4000/api/gantt/${task.id}`, { headers });
            await fetchTasks();
        } catch (err) {
            console.error("Error al eliminar la tarea:", err);
            alert(err.response?.data?.error || "Error al eliminar la tarea");
        } finally {
            setActionLoading(false);
        }
    };

    const handleClearGantt = async () => {
        if (!canWrite) {
            alert("Modo Demo Activo: Las modificaciones y escrituras están deshabilitadas de forma segura.");
            return;
        }
        if (!window.confirm("¿Estás seguro de que deseas vaciar todo el cronograma de Gantt para este proyecto? Esta acción no se puede deshacer y desvinculará todos los conceptos asociados.")) {
            return;
        }

        setActionLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': activeProjectId
            };
            await axios.delete('http://localhost:4000/api/gantt/clear', { headers });
            await fetchTasks();
        } catch (err) {
            console.error("Error al vaciar el cronograma:", err);
            alert(err.response?.data?.error || "Error al vaciar el cronograma");
        } finally {
            setActionLoading(false);
        }
    };

    const handleSeedGantt = async () => {
        if (!canWrite) {
            alert("Modo Demo Activo: Las modificaciones y escrituras están deshabilitadas de forma segura.");
            return;
        }
        setActionLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': activeProjectId
            };
            await axios.post('http://localhost:4000/api/gantt/seed', {}, { headers });
            await fetchTasks();
        } catch (err) {
            console.error("Error al sembrar la plantilla:", err);
            alert(err.response?.data?.error || "Error al sembrar la plantilla");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(37,99,235,0.15),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans relative overflow-hidden animate-fade-in">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2 flex items-center gap-3">
                            <Calendar className="text-blue-500" size={36} /> Planificación y Gantt Interactivo
                        </h1>
                        <p className="text-theme-text-secondary font-medium">
                            Monitorea el cronograma de obra y audita visualmente el progreso haciendo clic en las tareas programadas.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="bg-theme-panel/80 px-4 py-2 rounded-2xl border border-theme-border text-xs font-bold text-theme-text-secondary uppercase">
                            Obra: {activeProjectName}
                        </div>
                        {canWrite ? (
                            <>
                                {tasks.length > 0 && (
                                    <button 
                                        onClick={handleClearGantt}
                                        disabled={actionLoading}
                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 px-4 py-2 rounded-2xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <Trash2 size={14} /> {actionLoading ? 'Vaciando...' : 'Vaciar Cronograma'}
                                    </button>
                                )}
                                <button 
                                    onClick={openAddModal}
                                    className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-2xl text-xs font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <Plus size={16} /> Nueva Fase
                                </button>
                            </>
                        ) : isDemo ? (
                            <div className="bg-yellow-500/10 border border-yellow-500/20 px-4 py-2 rounded-2xl text-[10px] font-bold text-yellow-500 flex items-center gap-1.5 animate-pulse">
                                <AlertTriangle size={12} /> Modo Demo Activo (Lectura)
                            </div>
                        ) : (
                            <div className="bg-blue-500/10 border border-blue-500/20 px-4 py-2 rounded-2xl text-[10px] font-bold text-blue-400 flex items-center gap-1.5">
                                <Info size={12} /> Modo Lector (Solo Vista)
                            </div>
                        )}
                    </div>
                </div>

                {/* Info Tip */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 mb-8 flex items-center gap-3 text-xs text-blue-400 font-medium">
                    <Info size={20} className="shrink-0" />
                    <span><strong>Tip Operativo:</strong> Haz clic en cualquier barra de la gráfica de Gantt para abrir el registro fotográfico y verificar el avance visual de esa fase en particular.</span>
                </div>

                {/* Empty State vs Timeline */}
                {tasks.length === 0 ? (
                    <div className="bg-theme-panel p-12 rounded-3xl border border-theme-border text-center shadow-2xl backdrop-blur-xl max-w-2xl mx-auto my-12 animate-fade-in">
                        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 bg-blue-500/10 border border-blue-500/20">
                            <Calendar size={36} className="text-blue-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-theme-text-primary mb-3">Cronograma de Obra Vacío</h3>
                        <p className="text-theme-text-secondary mb-8 text-sm max-w-md mx-auto leading-relaxed">
                            No hay fases de construcción registradas en este proyecto. Puedes sembrar la plantilla por defecto vinculada a los conceptos o comenzar a estructurar el cronograma desde cero.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                            {canWrite ? (
                                <>
                                    <button 
                                        onClick={handleSeedGantt}
                                        disabled={actionLoading}
                                        className="w-full sm:w-auto bg-theme-bg hover:bg-theme-border text-theme-text-primary border border-theme-border px-6 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 hover:scale-[1.02] cursor-pointer"
                                    >
                                        <Sparkles size={16} className="text-blue-500" />
                                        {actionLoading ? 'Sembrando...' : 'Sembrar Plantilla Demo'}
                                    </button>
                                    <button 
                                        onClick={openAddModal}
                                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-2xl font-bold text-sm shadow-lg shadow-blue-500/20 transition-all flex items-center justify-center gap-2 hover:scale-[1.02] cursor-pointer"
                                    >
                                        <Plus size={16} /> Agregar Primera Fase
                                    </button>
                                </>
                            ) : (
                                <div className="text-xs text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 px-4 py-2.5 rounded-2xl font-semibold flex items-center gap-2">
                                    <AlertTriangle size={14} /> Como usuario Invitado Demo, no puedes inicializar ni editar el cronograma.
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* Gantt Card */
                    <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border shadow-2xl backdrop-blur-xl overflow-x-auto">
                        <div className="min-w-[800px]">
                            {/* Gantt Timeline Header */}
                            <div className="grid grid-cols-12 border-b border-theme-border pb-4 mb-4 text-center font-bold text-xs text-theme-text-secondary uppercase tracking-wider">
                                <div className="col-span-3 text-left pl-4">Fases de la Obra</div>
                                <div className="col-span-1">Acciones</div>
                                <div className="col-span-1">Progreso</div>
                                <div className="col-span-7">Junio 2026 (Línea de Tiempo)</div>
                            </div>

                            {/* Timeline Days labels */}
                            <div className="grid grid-cols-12 mb-2 text-[10px] font-bold text-slate-500 text-center">
                                <div className="col-span-3"></div>
                                <div className="col-span-1"></div>
                                <div className="col-span-1"></div>
                                <div className="col-span-7 grid grid-cols-30 gap-px">
                                    {Array.from({ length: 30 }).map((_, i) => (
                                        <div key={i} className="text-center">{i + 1}</div>
                                    ))}
                                </div>
                            </div>

                            {/* Tasks Rows */}
                            <div className="space-y-4">
                                {tasks.map((task) => {
                                    // Calculate position offset and width of the bar
                                    const startDay = parseInt(task.start.split('-')[2]);
                                    const endDay = parseInt(task.end.split('-')[2]);
                                    const duration = endDay - startDay + 1;
                                    
                                    const gridStart = startDay; // 1-indexed day offset
                                    const gridSpan = duration;

                                    return (
                                        <div 
                                            key={task.id} 
                                            onClick={() => handleTaskClick(task)}
                                            className="grid grid-cols-12 items-center hover:bg-theme-border/20 py-2.5 rounded-2xl cursor-pointer transition-colors border border-transparent hover:border-theme-border pl-4 group/row"
                                        >
                                            {/* Task Name */}
                                            <div className="col-span-3 pr-4">
                                                <h4 className="font-bold text-sm text-theme-text-primary leading-tight flex items-center gap-2">
                                                    {task.status === 'completed' ? (
                                                        <CheckCircle size={15} className="text-green-500" />
                                                    ) : task.status === 'active' ? (
                                                        <Clock size={15} className="text-blue-400 animate-pulse" />
                                                    ) : (
                                                        <Clock size={15} className="text-slate-500" />
                                                    )}
                                                    {task.name}
                                                </h4>
                                                <p className="text-[10px] text-theme-text-secondary mt-1 font-mono">
                                                    {task.start} al {task.end}
                                                </p>
                                            </div>

                                            {/* Actions (Edit / Delete) */}
                                            <div className="col-span-1 flex items-center gap-1.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                                {canWrite ? (
                                                    <>
                                                        <button 
                                                            onClick={(e) => openEditModal(task, e)}
                                                            title="Editar fase"
                                                            className="p-1.5 rounded-lg bg-theme-bg hover:bg-blue-500/10 text-theme-text-secondary hover:text-blue-400 border border-theme-border hover:border-blue-500/20 transition-all cursor-pointer"
                                                        >
                                                            <Edit size={12} />
                                                        </button>
                                                        <button 
                                                            onClick={(e) => handleDeleteTask(task, e)}
                                                            title="Eliminar fase"
                                                            className="p-1.5 rounded-lg bg-theme-bg hover:bg-red-500/10 text-theme-text-secondary hover:text-red-400 border border-theme-border hover:border-red-500/20 transition-all cursor-pointer"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className="text-[10px] text-theme-text-secondary italic">Lectura</span>
                                                )}
                                            </div>

                                            {/* Progress Badge */}
                                            <div className="col-span-1 text-center font-bold">
                                                <span className={`text-[10px] font-black px-2 py-1 rounded ${
                                                    task.progress === 100 ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                                                    task.progress > 0 ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                                    'bg-slate-500/10 text-slate-500 border border-theme-border'
                                                }`}>
                                                    {task.progress}%
                                                </span>
                                            </div>

                                            {/* Gantt Bar Chart Grid */}
                                            <div className="col-span-7 grid grid-cols-30 gap-px h-8 relative bg-theme-bg/30 rounded-lg border border-theme-border overflow-hidden">
                                                {/* Task Bar */}
                                                <div 
                                                    style={{ 
                                                        gridColumnStart: gridStart, 
                                                        gridColumnEnd: gridStart + gridSpan 
                                                    }}
                                                    className={`h-full relative flex items-center justify-start pl-2 rounded-md shadow-lg transition-transform hover:scale-[1.01] ${
                                                        task.status === 'completed' ? 'bg-gradient-to-r from-emerald-600/80 to-teal-500/80 border-l-4 border-emerald-400' :
                                                        task.status === 'active' ? 'bg-gradient-to-r from-blue-600/80 to-indigo-500/80 border-l-4 border-blue-400' :
                                                        'bg-slate-700/30 border-l-4 border-slate-500'
                                                    }`}
                                                >
                                                    {/* Progress Bar inside Task Bar */}
                                                    <div 
                                                        style={{ width: `${task.progress}%` }} 
                                                        className="absolute inset-y-0 left-0 bg-white/10 pointer-events-none"
                                                    ></div>
                                                    <span className="text-[9px] font-black text-white uppercase tracking-widest z-10 select-none">
                                                        {duration} d
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* SIDE DRAWER FOR SITE PHOTOS */}
            {drawerOpen && (
                <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-theme-panel/95 backdrop-blur-xl border-l border-theme-border shadow-2xl z-50 flex flex-col transition-all duration-300">
                    {/* Drawer Header */}
                    <div className="p-6 border-b border-theme-border flex justify-between items-center bg-gradient-to-b to-transparent">
                        <div>
                            <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Avance Fotográfico</span>
                            <h3 className="text-lg font-bold text-theme-text-primary leading-tight mt-1">
                                {selectedTask?.name}
                            </h3>
                            <p className="text-[10px] text-theme-text-secondary mt-1 font-mono">
                                Periodo: {selectedTask?.start} al {selectedTask?.end}
                            </p>
                        </div>
                        <button 
                            onClick={() => setDrawerOpen(false)}
                            className="p-2 rounded-xl bg-theme-bg hover:bg-theme-border text-theme-text-secondary hover:text-theme-text-primary transition-all cursor-pointer"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Drawer Body */}
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {loadingPhotos ? (
                            <div className="h-full flex items-center justify-center">
                                <LoaderGif />
                            </div>
                        ) : taskPhotos.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center text-theme-text-secondary">
                                <ImageIcon size={48} className="text-slate-500 mb-4 opacity-50" />
                                <p className="font-bold text-theme-text-primary text-sm">Sin capturas en este rango</p>
                                <p className="text-xs text-theme-text-secondary mt-1 max-w-xs">No hay fotos en el feed de telemetría correspondientes a las fechas de esta fase.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-6">
                                {taskPhotos.map((photo) => (
                                    <div key={photo.id} className="bg-theme-bg rounded-2xl overflow-hidden border border-theme-border shadow-md group relative">
                                        <img 
                                            src={formatImageUrl(photo.file_path)} 
                                            alt="Visual log" 
                                            className="w-full h-56 object-cover group-hover:scale-[1.01] transition-transform" 
                                        />
                                        <div className="p-4">
                                            <div className="flex justify-between items-center text-xs font-mono text-theme-text-secondary">
                                                <span>Captura #{photo.id}</span>
                                                <span>{new Date(photo.captured_at).toLocaleString()}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-[9px] font-black uppercase bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20">
                                                    Vision AI
                                                </span>
                                                <span className="text-xs font-bold text-theme-text-primary">
                                                    {photo.person_count || 0} Operarios Detectados
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ADD / EDIT TASK MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-theme-bg/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] shadow-2xl animate-fade-in animate-duration-200">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-theme-border flex justify-between items-center bg-gradient-to-b to-transparent">
                            <h2 className="text-xl font-bold text-theme-text-primary flex items-center gap-2">
                                <Calendar className="text-blue-500" />
                                {editingTask ? 'Editar Fase' : 'Nueva Fase de Obra'}
                            </h2>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 rounded-xl bg-theme-bg hover:bg-theme-border text-theme-text-secondary hover:text-theme-text-primary transition-all cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto space-y-4">
                            {errorMsg && (
                                <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl text-xs font-semibold flex items-center gap-2">
                                    <AlertTriangle size={14} className="shrink-0" />
                                    <span>{errorMsg}</span>
                                </div>
                            )}

                            <form id="ganttTaskForm" onSubmit={handleFormSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">
                                        Nombre de la Fase *
                                    </label>
                                    <input 
                                        type="text" 
                                        required 
                                        value={taskName} 
                                        onChange={(e) => setTaskName(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm font-semibold"
                                        placeholder="Ej. Cimentación y Columnas"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">
                                            Fecha de Inicio *
                                        </label>
                                        <input 
                                            type="date" 
                                            required 
                                            min="2026-06-01"
                                            max="2026-06-30"
                                            value={startDate} 
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm font-semibold font-mono"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">
                                            Fecha de Fin *
                                        </label>
                                        <input 
                                            type="date" 
                                            required 
                                            min="2026-06-01"
                                            max="2026-06-30"
                                            value={endDate} 
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm font-semibold font-mono"
                                        />
                                    </div>
                                </div>

                                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-[10px] text-blue-400 font-medium">
                                    💡 <strong>Nota del Demo:</strong> Para este visor, selecciona fechas entre el <strong>1 y el 30 de Junio de 2026</strong>.
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2">
                                            Estado
                                        </label>
                                        <select 
                                            value={taskStatus} 
                                            onChange={(e) => setTaskStatus(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border text-theme-text-primary px-4 py-3 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-sm font-semibold"
                                        >
                                            <option value="planned">Planificado</option>
                                            <option value="active">En Progreso</option>
                                            <option value="completed">Completado</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-theme-text-secondary uppercase tracking-wider mb-2 flex justify-between">
                                            <span>Progreso Manual</span>
                                            <span className="text-blue-500 font-black">{taskProgress}%</span>
                                        </label>
                                        <div className="flex items-center gap-3">
                                            <input 
                                                type="range" 
                                                min="0" 
                                                max="100" 
                                                step="5"
                                                value={taskProgress} 
                                                onChange={(e) => setTaskProgress(parseInt(e.target.value, 10))}
                                                className="flex-1 accent-blue-500 cursor-pointer"
                                            />
                                        </div>
                                        <span className="text-[9px] text-theme-text-secondary block mt-1">
                                            * Nota: Si vinculas conceptos en estimaciones, el progreso se calcula automáticamente de forma dinámica.
                                        </span>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-theme-border bg-theme-panel flex justify-end gap-3">
                            <button 
                                type="button" 
                                onClick={() => setIsModalOpen(false)}
                                className="px-5 py-2.5 rounded-xl font-bold text-theme-text-secondary hover:text-theme-text-primary transition-colors text-xs cursor-pointer"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="submit" 
                                form="ganttTaskForm" 
                                disabled={formLoading}
                                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-lg text-xs flex items-center gap-1.5 cursor-pointer"
                            >
                                {formLoading ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" /> Guardando...
                                    </>
                                ) : (
                                    'Guardar Fase'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* FLOATING ACTION BUTTON (FAB) FOR ADDING TASK */}
            {canWrite && (
                <button 
                    onClick={openAddModal}
                    title="Agregar Nueva Fase"
                    className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all z-40 cursor-pointer flex items-center justify-center border border-blue-400/20 group"
                >
                    <Plus size={24} />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out whitespace-nowrap text-sm font-bold">
                        Nueva Fase
                    </span>
                </button>
            )}
        </div>
    );
};

// Simple inline loader spinner helper
const LoaderGif = () => (
    <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-2"></div>
        <p className="text-xs font-medium text-theme-text-secondary">Cargando fotos del periodo...</p>
    </div>
);

export default GanttDashboard;
